/**
 * TopBannerHost / TopBanner — the ONE top-anchored overlay strip, and the slot
 * every app-level top banner renders into.
 *
 * Why a host instead of one `<ViewportLayer anchor="top">` per banner: two such
 * layers both sit at the visible top edge, so the second one paints exactly on
 * top of the first. The update banner and the PWA install banner can be up at
 * the same moment — a gym the user has not installed yet, on a tab that has
 * been open long enough for a deploy to land — and whichever rendered last hid
 * the other. One layer, several children, and `ViewportLayer`'s own `gap`
 * stacks them.
 *
 * The banners cannot simply be siblings in the App tree: the update banner is
 * mounted at the root, while the install banner needs the gym state that only
 * lives inside `MainApp`. So the host publishes its strip node through context
 * and `TopBanner` portals its card in from wherever it is rendered. React
 * context follows the REACT tree, not the DOM, so a card portalled out of
 * `MainApp` still reads the gym's accent rather than the platform one.
 *
 * Stack order is `order`, not mount order: a portal appends in whatever order
 * the two subtrees happen to commit, and the install banner mounts a route
 * deeper than the update banner. Lower number = higher on screen.
 */
import React, { createContext, useContext, useState } from "react";
import ReactDOM from "react-dom";
import { ViewportLayer } from "./ui";
import { F } from "../constants";
import { useAppTheme } from "../contexts/ThemeContext";

/** Slot order — the update notice stays on top: it cannot be dismissed, and
 *  running stale code is the more urgent of the two messages. */
export const TOP_BANNER_ORDER = { update: 0, install: 1 };

const TopBannerCtx = createContext(null);

/** Card width for every top banner, so the stack reads as one column. */
const BANNER_MAX_WIDTH = 460;

export function TopBannerHost({ children }) {
  // State, not a ref: the slots must re-render once the strip node exists.
  const [host, setHost] = useState(null);
  return (
    <>
      <ViewportLayer anchor="top" zIndex={2001}>
        {/* The inner column is the portal target — ViewportLayer owns its own
            node, and handing that one out would let a child style collide
            with the visual-viewport pin writing to it every pan frame. */}
        <div ref={setHost} style={{
          width: "100%", display: "flex", flexDirection: "column",
          alignItems: "center", gap: 8,
        }} />
      </ViewportLayer>
      <TopBannerCtx.Provider value={host}>{children}</TopBannerCtx.Provider>
    </>
  );
}

/**
 * One banner in the top stack. Renders nothing until the host is mounted.
 *
 * @param {number} order - from TOP_BANNER_ORDER; lower sits higher.
 */
export function TopBanner({ order = 0, children }) {
  const host = useContext(TopBannerCtx);
  if (!host) return null;
  return ReactDOM.createPortal(
    <div style={{
      order,
      // The strip is pointer-events: none so the app under it stays clickable.
      pointerEvents: "auto",
      width: "100%", maxWidth: BANNER_MAX_WIDTH,
    }}>{children}</div>,
    host,
  );
}

/**
 * TopBannerCard — the look every top banner wears, in ONE place: the update
 * notice and the install invitation are the same card by design, and two
 * copies of the style drifted apart the moment one of them was retuned.
 *
 * The shadow is deliberately heavier than a normal card's. This card floats
 * over whatever the page happens to be showing, with no backdrop behind it —
 * and it is accent-coloured, so on a gym page the app chrome under it is
 * already tinted the SAME colour and a soft shadow left it reading as part of
 * the header rather than as a thing on top of it. Two layers do the work: a
 * tight dark one for the edge, a wide soft one for the lift. The 1px light
 * ring is what keeps the edge visible on a dark background, where a shadow
 * has nothing left to darken.
 */
export function TopBannerCard({ role = "status", children }) {
  const { accent } = useAppTheme();
  return (
    <div role={role} aria-live="polite" style={{
      background: accent, color: "#fff", padding: "10px 16px",
      borderRadius: 14,
      border: "1px solid rgba(255,255,255,0.22)",
      boxShadow: "0 1px 3px rgba(0,0,0,0.30), 0 10px 30px rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
      fontSize: 13, fontWeight: 600, fontFamily: F,
    }}>{children}</div>
  );
}

/** The inline action on a top banner — outlined, never filled: the card is
 *  already the accent, so a GreenBtn would vanish into it. */
export function TopBannerAction({ onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: "6px 16px", borderRadius: 8, border: "2px solid #fff",
      background: "transparent", color: "#fff", fontSize: 12, fontWeight: 700,
      cursor: "pointer", fontFamily: F, whiteSpace: "nowrap",
    }}>{children}</button>
  );
}
